/**
 * Client-side world state.
 *
 * The local avatar is simulated locally (see input.ts) and only corrected by
 * the server; remote avatars are interpolated towards the latest snapshot so
 * 15Hz updates still look smooth at 60fps.
 */

import type {
  Capabilities,
  ChatMessage,
  Note,
  OfficeMap,
  Player,
  PlayerState,
  Group,
  Notification,
  RoomAccess,
  Task,
  TaskCategory,
} from '@virtual-office/shared';

export interface ClientPlayer extends Player {
  /** Smoothed position actually drawn this frame. */
  renderX: number;
  renderY: number;
  /** Latest authoritative position from the server. */
  targetX: number;
  targetY: number;
  /** Speech bubble shown above the head, with its expiry. */
  bubble: { text: string; until: number } | null;
}

type Listener = () => void;

export type StateEvent =
  | 'welcome'
  | 'players'
  | 'notes'
  | 'chat'
  | 'zone'
  | 'access'
  | 'notifications'
  | 'groups'
  | 'tasks'
  | 'connection';

export class Store {
  map: OfficeMap | null = null;
  capabilities: Capabilities | null = null;
  meId: string | null = null;
  players = new Map<string, ClientPlayer>();
  notes = new Map<string, Note>();
  messages: ChatMessage[] = [];
  connection: 'connecting' | 'online' | 'offline' = 'connecting';
  /** Which rooms this viewer may enter, keyed by zone id. */
  access = new Map<string, RoomAccess>();
  /** Newest first. */
  notifications: Notification[] = [];
  /** Groups you may mention: global ones plus your own tags. */
  groups: Group[] = [];
  /**
   * The task board, keyed by id.
   *
   * Fetched over HTTP when the panel first opens and kept current by pushes
   * over the socket, so two people working the same board see each other's
   * edits without refreshing.
   */
  tasks = new Map<string, Task>();
  /** Whether the board has been fetched at least once. */
  tasksLoaded = false;
  /** Admin-managed task categories, newest fetch wins. */
  categories: TaskCategory[] = [];
  /** Round-trip time in ms, or null before the first pong. */
  latency: number | null = null;

  private listeners = new Map<StateEvent, Set<Listener>>();

  on(event: StateEvent, listener: Listener): () => void {
    const set = this.listeners.get(event) ?? new Set<Listener>();
    set.add(listener);
    this.listeners.set(event, set);
    return () => set.delete(listener);
  }

  emit(event: StateEvent): void {
    for (const listener of this.listeners.get(event) ?? []) listener();
  }

  get me(): ClientPlayer | null {
    return this.meId ? (this.players.get(this.meId) ?? null) : null;
  }

  get others(): ClientPlayer[] {
    return [...this.players.values()].filter((p) => p.id !== this.meId);
  }

  toClientPlayer(player: Player): ClientPlayer {
    return {
      ...player,
      renderX: player.x,
      renderY: player.y,
      targetX: player.x,
      targetY: player.y,
      bubble: null,
    };
  }

  setPlayers(players: Player[]): void {
    this.players = new Map(players.map((p) => [p.id, this.toClientPlayer(p)]));
    this.emit('players');
  }

  upsertPlayer(player: Player): void {
    const existing = this.players.get(player.id);
    if (existing) {
      Object.assign(existing, player, { targetX: player.x, targetY: player.y });
    } else {
      this.players.set(player.id, this.toClientPlayer(player));
    }
    this.emit('players');
  }

  removePlayer(id: string): void {
    if (this.players.delete(id)) this.emit('players');
  }

  /**
   * Apply a server snapshot. The local avatar is left alone.
   *
   * Most snapshots are partial — only movers, and mostly only your own room —
   * so absence from one means nothing. Only a `full` snapshot is allowed to
   * prune; pruning on a partial one would delete everybody outside your room
   * from the roster fifteen times a second.
   */
  applySnapshot(states: PlayerState[], full = false): void {
    const seen = new Set<string>();
    let rosterChanged = false;

    for (const state of states) {
      seen.add(state.id);
      const player = this.players.get(state.id);
      if (!player) continue;
      if (player.id === this.meId) {
        // Trust local prediction for position, but the server owns the zone.
        if (player.zoneId !== state.zoneId) {
          player.zoneId = state.zoneId;
          this.emit('zone');
        }
        continue;
      }
      if (player.zoneId !== state.zoneId) rosterChanged = true;
      player.targetX = state.x;
      player.targetY = state.y;
      player.facing = state.facing;
      player.moving = state.moving;
      player.zoneId = state.zoneId;
    }

    if (full) {
      for (const id of [...this.players.keys()]) {
        if (!seen.has(id)) {
          this.players.delete(id);
          rosterChanged = true;
        }
      }
    }
    if (rosterChanged) this.emit('players');
  }

  /** Ease remote avatars towards their snapshot position. */
  interpolate(dt: number): void {
    // Exponential smoothing: fast enough to feel responsive, slow enough to hide jitter.
    const alpha = 1 - Math.exp(-dt * 14);
    for (const player of this.players.values()) {
      if (player.id === this.meId) {
        player.renderX = player.x;
        player.renderY = player.y;
        continue;
      }
      player.renderX += (player.targetX - player.renderX) * alpha;
      player.renderY += (player.targetY - player.renderY) * alpha;
      player.x = player.renderX;
      player.y = player.renderY;
    }
  }

  setAccess(rooms: RoomAccess[]): void {
    this.access = new Map(rooms.map((room) => [room.zoneId, room]));
    this.emit('access');
  }

  /** Locked rooms still render — you can see the door, you just cannot use it. */
  canEnter(zoneId: string): boolean {
    return this.access.get(zoneId)?.allowed ?? true;
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => n.readAt === null).length;
  }

  setNotifications(list: Notification[]): void {
    this.notifications = [...list].sort((a, b) => b.createdAt - a.createdAt);
    this.emit('notifications');
  }

  addNotification(notification: Notification): void {
    this.notifications = [notification, ...this.notifications].slice(0, 100);
    this.emit('notifications');
  }

  /** Local echo of marking read; the server is told separately. */
  markAllRead(): void {
    const now = Date.now();
    this.notifications = this.notifications.map((n) =>
      n.readAt === null ? { ...n, readAt: now } : n,
    );
    this.emit('notifications');
  }

  setCategories(list: TaskCategory[]): void {
    this.categories = list;
    this.emit('tasks');
  }

  setTasks(list: Task[]): void {
    this.tasks = new Map(list.map((task) => [task.id, task]));
    this.tasksLoaded = true;
    this.emit('tasks');
  }

  /** Upsert, because a push is the same message whether new or edited. */
  putTask(task: Task): void {
    this.tasks.set(task.id, task);
    this.emit('tasks');
  }

  removeTask(id: string): void {
    if (this.tasks.delete(id)) this.emit('tasks');
  }

  setGroups(groups: Group[]): void {
    this.groups = groups;
    this.emit('groups');
  }

  addMessage(message: ChatMessage): void {
    this.messages.push(message);
    if (this.messages.length > 500) this.messages.splice(0, this.messages.length - 500);

    const author = this.players.get(message.authorId);
    if (author) {
      author.bubble = { text: message.body.slice(0, 120), until: performance.now() + 6000 };
    }
    this.emit('chat');
  }

  /** Merge scrollback without duplicating messages already shown. */
  mergeHistory(messages: ChatMessage[]): void {
    if (messages.length === 0) return;
    const known = new Set(this.messages.map((m) => m.id));
    const added = messages.filter((m) => !known.has(m.id));
    if (added.length === 0) return;
    this.messages = [...this.messages, ...added].sort((a, b) => a.createdAt - b.createdAt);
    this.emit('chat');
  }

  setNotes(notes: Note[]): void {
    this.notes = new Map(notes.map((n) => [n.id, n]));
    this.emit('notes');
  }

  upsertNote(note: Note): void {
    const existing = this.notes.get(note.id);
    // Ignore an echo of an older revision arriving after a newer one.
    if (existing && existing.revision > note.revision) return;
    this.notes.set(note.id, note);
    this.emit('notes');
  }

  removeNote(id: string): void {
    if (this.notes.delete(id)) this.emit('notes');
  }

  notesFor(surfaceId: string): Note[] {
    return [...this.notes.values()]
      .filter((n) => n.surfaceId === surfaceId)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }
}
